import { Box, Text, Flex, Button, useDisclosure } from '@chakra-ui/react'
import { addMinutes, format } from 'date-fns'
import React from 'react'
import { DEFAULT_TIME_ZONE } from '~config/constants'
import { useClinic } from '~contexts/clinic-context'
import RescheduleModal from '~components/appointments/reschedule-modal'
import AppointmentDetailsDrawer from '~components/shared/modals/appointment-details/appointment-details-drawer'
import { ActionItemFieldsFragment } from '~graphql/generated/graphql'
import { stringIfExists } from '~utils/helpers'
import { SharedAssignee } from './shared'
import { formatInTimeZone } from 'date-fns-tz'

export default function RescheduleContent({
    actionItem,
}: {
    actionItem: ActionItemFieldsFragment
}) {
    const {
        onOpen: onDetailsOpen,
        onClose: onDetailsClose,
        isOpen: isDetailsOpen,
    } = useDisclosure()
    const {
        onOpen: onConfirmOpen,
        onClose: onConfirmClose,
        isOpen: isConfirmOpen,
    } = useDisclosure()

    const { clinic } = useClinic()

    if (!actionItem.reschedule) {
        return (
            <Box mt={1}>
                <Text
                    fontSize="sm"
                    color="gray.500"
                    fontStyle="italic"
                    fontWeight="semibold"
                >
                    This appointment has been resolved!
                </Text>
            </Box>
        )
    }

    const {
        appointment,
        requested_start_time,
        requested_clinician,
        original_start_time,
        original_clinician,
    } = actionItem.reschedule

    const { service, service_name, client, patient, minutes } = appointment

    const isNewClinician =
        requested_clinician &&
        requested_clinician.name !== original_clinician?.name

    return (
        <Box w="full">
            <Flex mt={1} justify="space-between" direction="column" gap={3}>
                <Flex justify="space-between">
                    <Box>
                        <Text fontSize="md" fontWeight="semibold">
                            {service?.name || service_name}{' '}
                            {stringIfExists(
                                requested_clinician?.name ||
                                    original_clinician?.name,
                                {
                                    pretext: 'with ',
                                },
                            )}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                            {stringIfExists(client?.first_name, {
                                posttext:
                                    stringIfExists(client?.last_name, {
                                        pretext: ' ',
                                    }) + ', ',
                            })}
                            {stringIfExists(patient?.name, {
                                posttext: stringIfExists(
                                    patient?.clinic_species?.species,
                                    { pretext: ' (', posttext: ')' },
                                ),
                            })}
                        </Text>
                    </Box>
                    {!!actionItem.assignee && (
                        <SharedAssignee assignee={actionItem.assignee} />
                    )}
                </Flex>
                <Box fontSize="sm">
                    <Text textDecor="line-through">
                        Current Time:{' '}
                        {formatInTimeZone(
                            original_start_time || appointment.start_time,
                            clinic?.time_zone ?? DEFAULT_TIME_ZONE,
                            'h:mm a',
                        )}{' '}
                        -{' '}
                        {formatInTimeZone(
                            addMinutes(
                                original_start_time || appointment.start_time,
                                minutes,
                            ),
                            clinic?.time_zone ?? DEFAULT_TIME_ZONE,
                            "h:mm a 'on' MMMM d",
                        )}
                    </Text>
                    <Text>
                        Requested Time:{' '}
                        {formatInTimeZone(
                            requested_start_time || appointment.start_time,
                            clinic?.time_zone ?? DEFAULT_TIME_ZONE,
                            'h:mm a',
                        )}{' '}
                        -{' '}
                        {formatInTimeZone(
                            addMinutes(
                                requested_start_time || appointment.start_time,
                                minutes,
                            ),
                            clinic?.time_zone ?? DEFAULT_TIME_ZONE,
                            "h:mm a 'on' MMMM d",
                        )}
                    </Text>
                </Box>
                {isNewClinician && (
                    <Box fontSize="sm">
                        <Text textDecor="line-through">
                            Original Clinician: {original_clinician?.name}
                        </Text>
                        <Text>
                            Requested Clinician: {requested_clinician?.name}
                        </Text>
                    </Box>
                )}
                <Flex gap={3}>
                    <Button size="xs" onClick={onDetailsOpen}>
                        View Details
                    </Button>
                    <Button
                        size="xs"
                        colorScheme="brand"
                        onClick={onConfirmOpen}
                    >
                        Review & Confirm
                    </Button>
                </Flex>
            </Flex>
            <AppointmentDetailsDrawer
                appointment={appointment}
                reschedule={actionItem.reschedule}
                isOpen={isDetailsOpen}
                onClose={onDetailsClose}
            />
            <RescheduleModal
                isOpen={isConfirmOpen}
                onClose={onConfirmClose}
                reschedule={actionItem.reschedule}
            />
        </Box>
    )
}
